import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  deleteField,
  limit
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from './firebase';
import { loadIndividualQuestions, loadJointQuestions } from './questions-firestore';
import { getUserProfile, hasPurchasedSet } from './admin';
import { ClientEncryption, isPIIField, shouldEncrypt } from './encryption-utils';
import { getCurrentUser, getEncryptionKey } from './auth';
import { deleteUser as firebaseDeleteUser } from 'firebase/auth';
import { isFreeQuestion } from './free-questions';

// ============= INTERFACES =============

export interface QuestionResponse {
  questionId: string;
  value: string | string[];
  updatedAt: string;
  isPrivate?: boolean;              // Privacy flag
  sharedWithPartner?: boolean;      // Explicit sharing flag
  encrypted?: boolean;              // PII encryption flag
}

export interface SectionProgress {
  sectionId: string;
  totalQuestions: number;
  answeredQuestions: number;
  completed: boolean;
  lastUpdated: string;
}

export interface UserResponses {
  userId: string;
  createdAt: string;
  lastUpdated: string;
  responses: Record<string, QuestionResponse>;
  progress: Record<string, SectionProgress>;
  relationshipId?: string | null;    // NEW: Link to relationship
  privateSections?: string[];        // Array of core topic IDs marked private (e.g., 'emotions-connection')
  onboardingCompleted?: boolean;     // NEW: Onboarding completion status
}

// NEW: Relationship interface
export interface Relationship {
  relationshipId: string;
  pairingCode: string;
  partner1Id: string;
  partner2Id: string | null;
  partner1Email: string;
  partner2Email: string | null;
  partner1Name?: string;  // Stored as plaintext string
  partner2Name?: string;  // Stored as plaintext string

  // Status tracking
  status: 'pending' | 'active' | 'completed' | 'cancelled';
  partner1Joined: boolean;
  partner2Joined: boolean;

  // Stage progression
  currentStage: 'individual' | 'comparison' | 'joint' | 'review' | 'signing' | 'completed';

  // Individual Work completion
  partner1IndividualComplete: boolean;
  partner2IndividualComplete: boolean;

  // Comparison acknowledgement
  partner1ComparisonAcknowledged: boolean;
  partner2ComparisonAcknowledged: boolean;
  comparisonAcknowledgedAt: string | null;

  // Joint collaboration acknowledgement
  partner1JointAcknowledged: boolean;
  partner2JointAcknowledged: boolean;
  jointAcknowledgedAt: string | null;

  // Questions flagged for future review (conflicting answers)
  flaggedQuestions?: string[];  // Array of question IDs flagged by either partner

  // Contract finalization
  contractFinalized?: boolean;
  contractFinalizedAt?: string | null;

  // Contract version history
  contractVersionCount?: number;

  // Comfort/Vulnerability Levels (1-3)
  // 1 = "Comfortable" - Basic topics only (logistics, values, daily life)
  // 2 = "Open" - Most topics including some sensitive areas (finances, family planning)
  // 3 = "Fully Vulnerable" - All topics including highly sensitive (past relationships, sex, substance use, mental health)
  partner1ComfortLevel?: number;
  partner2ComfortLevel?: number;

  // Question Selection - Random questions selected from each core topic
  questionSelection?: {
    individual: string[];  // Array of question IDs
    joint: string[];       // Array of question IDs
    createdAt: string;
    questionsPerTopic: number;
  };

  // Timestamps
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
}

// NEW: Comparison data interface
export interface ComparisonResult {
  questionId: string;
  questionText: string;
  questionType: string;
  category: string;  // DEPRECATED: Use coreTopics instead
  coreTopics?: string[];  // Core topics from tags (array of CoreTopic strings)

  // Response data
  partner1Value: string | string[] | null;
  partner2Value: string | string[] | null;

  // Privacy flags
  partner1Private: boolean;
  partner2Private: boolean;
  visibleToPartner1: boolean;
  visibleToPartner2: boolean;

  // Comparison status
  matches: boolean;
  needsDiscussion: boolean;
  flaggedForReview?: boolean;  // Either partner flagged this question for future discussion

  // --- Prediction items ---
  // Set only when the question asks "what do you think your partner answered?".
  // Each partner's GUESS is scored against the OTHER partner's actual answer to
  // predictsQuestionId — never against their own. A `*ActualValue` is null when
  // that partner kept the target question (or its topic) private, in which case
  // the corresponding `*PredictionCorrect` is left undefined rather than false.
  predictsQuestionId?: string;
  predictionTargetText?: string;
  partner1PredictedValue?: string | string[] | null;
  partner2ActualValue?: string | string[] | null;
  partner1PredictionCorrect?: boolean;
  partner2PredictedValue?: string | string[] | null;
  partner1ActualValue?: string | string[] | null;
  partner2PredictionCorrect?: boolean;
}

export interface ComparisonData {
  relationshipId: string;
  partner1Id: string;
  partner2Id: string;

  // Pre-computed comparison results
  comparisons: ComparisonResult[];

  // Statistics
  // matchingAnswers/conflictingAnswers count standard questions only —
  // prediction items are scored cross-partner and would otherwise register a
  // correct guess of a differing answer as a conflict.
  totalQuestions: number;
  matchingAnswers: number;
  conflictingAnswers: number;
  privateQuestions: number;

  // Prediction items, counted separately. predictionsTotal counts scoreable
  // guesses (both a guess and a visible target answer), so it can be less than
  // twice the number of prediction questions.
  predictionsCorrect?: number;
  predictionsTotal?: number;

  // Last update
  lastComputed: string;
  partner1LastUpdated: string;
  partner2LastUpdated: string;
}

// Helper to convert Firestore Timestamp to ISO string
function timestampToString(timestamp: unknown): string {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toISOString();
  }
  return (timestamp as string) || new Date().toISOString();
}

// ============= HELPER FUNCTIONS =============

/**
 * Get partner index (0 or 1) for a user within a relationship
 */
export async function getPartnerIndex(relationshipId: string, userId: string): Promise<number> {
  const relationship = await getRelationship(relationshipId);
  if (!relationship) {
    throw new Error('Relationship not found');
  }

  if (relationship.partner1Id === userId) {
    console.log('[getPartnerIndex] User is Partner 1, index: 0');
    return 0;
  } else if (relationship.partner2Id === userId) {
    console.log('[getPartnerIndex] User is Partner 2, index: 1');
    return 1;
  } else {
    throw new Error('User is not part of this relationship');
  }
}

/**
 * Get the Firestore document reference for user responses
 */
function getUserResponsesRef(relationshipId: string, partnerIndex: number) {
  return doc(db, 'userIndividualResponses', relationshipId, 'responses', partnerIndex.toString());
}

export async function saveResponse(
  relationshipId: string,
  userId: string,
  questionId: string,
  value: string | string[],
  isPrivate: boolean = false  // Privacy parameter
): Promise<void> {
  try {
    const partnerIndex = await getPartnerIndex(relationshipId, userId);
    const responsesRef = getUserResponsesRef(relationshipId, partnerIndex);
    const responses = await getUserResponses(relationshipId, userId);

    // Check if encryption is enabled and we have encryption key available
    let finalValue = value;
    let isEncrypted = false;
    const encryptionKey = getEncryptionKey();

    // Encrypt if: encryption is enabled globally AND field is PII AND we have a key
    if (shouldEncrypt() && isPIIField(questionId) && encryptionKey) {
      try {
        console.log(`🔒 Encrypting field: ${questionId}`);
        if (typeof value === 'string') {
          finalValue = await ClientEncryption.encrypt(value, encryptionKey);
          isEncrypted = true;
        } else if (Array.isArray(value)) {
          // Encrypt each item in the array (e.g., for person-list)
          finalValue = await Promise.all(
            value.map(async (item) => {
              if (typeof item === 'string') {
                return await ClientEncryption.encrypt(item, encryptionKey);
              }
              return item;
            })
          );
          isEncrypted = true;
        }
        console.log(`✅ Field encrypted successfully`);
      } catch (encryptionError) {
        console.error(`❌ Encryption failed for ${questionId}:`, encryptionError);
        // Fall back to saving unencrypted if encryption fails
      }
    }

    responses.responses[questionId] = {
      questionId,
      value: finalValue,
      updatedAt: new Date().toISOString(),
      isPrivate,
      sharedWithPartner: !isPrivate,
      encrypted: isEncrypted,
    };

    responses.lastUpdated = new Date().toISOString();

    await setDoc(responsesRef, {
      ...responses,
      lastUpdatedTimestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error saving response:', error);
    throw error;
  }
}

export async function getResponse(
  relationshipId: string,
  userId: string,
  questionId: string
): Promise<QuestionResponse | null> {
  const responses = await getUserResponses(relationshipId, userId);
  return responses.responses[questionId] || null;
}

export async function getUserResponses(
  relationshipId: string,
  userId: string
): Promise<UserResponses> {
  try {
    const partnerIndex = await getPartnerIndex(relationshipId, userId);
    const responsesRef = getUserResponsesRef(relationshipId, partnerIndex);
    const docSnap = await getDoc(responsesRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      console.log('[getUserResponses] Firestore data.progress:', data.progress);
      console.log('[getUserResponses] Number of progress entries:', Object.keys(data.progress || {}).length);

      const responses: Record<string, QuestionResponse> = data.responses || {};
      const encryptionKey = getEncryptionKey();

      // Decrypt PII fields if we have an encryption key
      if (encryptionKey) {
        for (const [questionId, response] of Object.entries(responses)) {
          if (response.encrypted && response.value) {
            try {
              console.log(`🔓 Decrypting PII field: ${questionId}`);
              if (typeof response.value === 'string') {
                response.value = await ClientEncryption.decrypt(response.value, encryptionKey);
              } else if (Array.isArray(response.value)) {
                // Decrypt each item in the array
                response.value = await Promise.all(
                  response.value.map(async (item: unknown): Promise<string> => {
                    if (typeof item === 'string') {
                      return await ClientEncryption.decrypt(item, encryptionKey);
                    }
                    return String(item);
                  })
                );
              }
              // Remove encrypted flag after successful decryption (for UI)
              delete response.encrypted;
              console.log(`✅ PII field decrypted successfully`);
            } catch (decryptionError) {
              console.error(`❌ Failed to decrypt ${questionId}:`, decryptionError);
              // Leave as encrypted and replace with placeholder
              response.value = '[Encrypted - Re-login Required]';
            }
          }
        }
      } else {
        // No encryption key available - mark encrypted fields
        for (const [questionId, response] of Object.entries(responses)) {
          if (response.encrypted) {
            console.log(`⚠️ Encrypted field ${questionId} found but no encryption key available`);
            response.value = '[Encrypted - Please Log In]';
          }
        }
      }

      return {
        userId,
        createdAt: timestampToString(data.createdAt),
        lastUpdated: timestampToString(data.lastUpdated),
        responses,
        progress: data.progress || {},
        relationshipId,
        privateSections: data.privateSections || [],
        onboardingCompleted: data.onboardingCompleted || false,
      };
    }

    // Create new document if it doesn't exist
    const newResponses: UserResponses = {
      userId,
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      responses: {},
      progress: {},
      relationshipId,
      privateSections: [],
      onboardingCompleted: false,
    };

    await setDoc(responsesRef, {
      ...newResponses,
      createdAtTimestamp: serverTimestamp(),
      lastUpdatedTimestamp: serverTimestamp(),
    });

    return newResponses;
  } catch (error) {
    console.error('Error getting user responses:', error);
    // Return empty responses on error
    return {
      userId,
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      responses: {},
      progress: {},
      relationshipId,
      privateSections: [],
      onboardingCompleted: false,
    };
  }
}

export async function updateSectionProgress(
  relationshipId: string,
  userId: string,
  sectionId: string,
  totalQuestions: number,
  answeredQuestions: number
): Promise<void> {
  try {
    const partnerIndex = await getPartnerIndex(relationshipId, userId);
    const responsesRef = getUserResponsesRef(relationshipId, partnerIndex);
    const responses = await getUserResponses(relationshipId, userId);

    responses.progress[sectionId] = {
      sectionId,
      totalQuestions,
      answeredQuestions,
      completed: answeredQuestions === totalQuestions,
      lastUpdated: new Date().toISOString(),
    };

    responses.lastUpdated = new Date().toISOString();

    console.log(`[updateSectionProgress] Saving progress for ${sectionId}:`, {
      totalQuestions,
      answeredQuestions,
      completed: answeredQuestions === totalQuestions,
      progressKeys: Object.keys(responses.progress)
    });

    await setDoc(responsesRef, {
      ...responses,
      lastUpdatedTimestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating section progress:', error);
    throw error;
  }
}

export async function getSectionProgress(
  relationshipId: string,
  userId: string,
  sectionId: string
): Promise<SectionProgress | null> {
  const responses = await getUserResponses(relationshipId, userId);
  return responses.progress[sectionId] || null;
}

export async function calculateOverallProgress(
  relationshipId: string,
  userId: string
): Promise<{ total: number; answered: number; percentage: number }> {
  const responses = await getUserResponses(relationshipId, userId);

  // Load all individual questions from Firestore
  const allIndividualQuestions = await loadIndividualQuestions();

  // Get user's comfort level to filter questions
  const comfortLevel = await getComfortLevel(relationshipId, userId);

  // Separate free and premium questions
  const freeQuestions = allIndividualQuestions.filter(q => !q.questionSet);
  const premiumQuestions = allIndividualQuestions.filter(q => !!q.questionSet);

  // Fixed free set: exactly the curated 24 questions, shown regardless of comfort level.
  const filteredQuestions = freeQuestions.filter(q => isFreeQuestion(q.id));

  // Add purchased premium sets, still comfort-filtered.
  const userProfile = await getUserProfile(userId);
  const purchasedPremium = premiumQuestions.filter(q => {
    if (!q.questionSet) return false;
    if (!hasPurchasedSet(userProfile, q.questionSet)) return false;
    return !q.comfortLevel || q.comfortLevel <= (comfortLevel || 3);
  });
  filteredQuestions.push(...purchasedPremium);

  const total = filteredQuestions.length;

  // Get privacy settings (now uses core topics instead of categories)
  const privateSections = new Set(responses.privateSections || []);

  // Import question tags helper
  const { getQuestionTags } = await import('./question-tags');

  // Count answered questions from filtered set (including private questions)
  const answered = filteredQuestions.filter(q => {
    const response = responses.responses[q.id];

    // Check if any core topic of this question is marked private
    const questionTags = getQuestionTags(q.id);
    const coreTopics = questionTags?.coreTopics || [];
    const isTopicPrivate = coreTopics.some(topic => privateSections.has(topic));

    if (isTopicPrivate) {
      return true; // Topic is private, counts as answered
    }

    // Check if individual question is private
    if (response?.isPrivate) {
      return true; // Question is private, counts as answered
    }

    // Check if question has a value
    return response?.value &&
           (Array.isArray(response.value) ? response.value.length > 0 : response.value.trim());
  }).length;

  console.log('[calculateOverallProgress] Comfort level:', comfortLevel);
  console.log('[calculateOverallProgress] Filtered questions:', filteredQuestions.length);
  console.log('[calculateOverallProgress] Private sections:', Array.from(privateSections));
  console.log('[calculateOverallProgress] Total:', total, 'Answered:', answered);

  return {
    total,
    answered,
    percentage: total > 0 ? Math.round((answered / total) * 100) : 0,
  };
}

export async function calculateProgress(
  relationshipId: string,
  userId: string,
  sectionId: string
): Promise<{ percentage: number; answered: number; total: number }> {
  const progress = await getSectionProgress(relationshipId, userId, sectionId);

  if (!progress) {
    return { percentage: 0, answered: 0, total: 0 };
  }

  return {
    percentage: progress.totalQuestions > 0 ? Math.round((progress.answeredQuestions / progress.totalQuestions) * 100) : 0,
    answered: progress.answeredQuestions,
    total: progress.totalQuestions,
  };
}

export async function exportResponses(
  relationshipId: string,
  userId: string
): Promise<string> {
  const responses = await getUserResponses(relationshipId, userId);
  return JSON.stringify(responses, null, 2);
}

export async function deleteAllUserData(
  relationshipId: string,
  userId: string
): Promise<void> {
  try {
    const partnerIndex = await getPartnerIndex(relationshipId, userId);
    const responsesRef = getUserResponsesRef(relationshipId, partnerIndex);

    await setDoc(responsesRef, {
      userId,
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      responses: {},
      progress: {},
      relationshipId,
      privateSections: [],
      onboardingCompleted: false,
      createdAtTimestamp: serverTimestamp(),
      lastUpdatedTimestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error deleting user data:', error);
    throw error;
  }
}

export async function markOnboardingComplete(
  relationshipId: string,
  userId: string
): Promise<void> {
  try {
    // Check if relationship is paired (active status) before allowing completion
    const relationship = await getRelationship(relationshipId);
    if (!relationship) {
      throw new Error('Relationship not found');
    }

    const partnerIndex = await getPartnerIndex(relationshipId, userId);
    const responsesRef = getUserResponsesRef(relationshipId, partnerIndex);

    await setDoc(responsesRef, {
      onboardingCompleted: true,
      lastUpdatedTimestamp: serverTimestamp(),
    }, { merge: true });

    console.log('[Storage] Onboarding marked complete for user:', userId);
  } catch (error) {
    console.error('Error marking onboarding complete:', error);
    throw error;
  }
}

export async function isOnboardingComplete(
  relationshipId: string,
  userId: string
): Promise<boolean> {
  try {
    const partnerIndex = await getPartnerIndex(relationshipId, userId);
    const responsesRef = getUserResponsesRef(relationshipId, partnerIndex);
    const docSnap = await getDoc(responsesRef);

    if (docSnap.exists()) {
      return docSnap.data().onboardingCompleted || false;
    }

    return false;
  } catch (error) {
    console.error('Error checking onboarding status:', error);
    return false;
  }
}

// ============= RELATIONSHIP MANAGEMENT =============

/**
 * Create a new relationship and generate pairing code
 */
export async function createRelationship(
  userId: string,
  userEmail: string,
  userName?: string
): Promise<{ relationshipId: string; pairingCode: string }> {
  try {
    // No validation here - users can freely create pairing codes
    // The relationship won't be "active" until someone joins with the code

    // Generate unique relationship ID and pairing code
    const relationshipId = `rel_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const pairingCode = generatePairingCode();

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7); // 7 days expiration

    const relationship: Partial<Relationship> = {
      relationshipId,
      pairingCode,
      partner1Id: userId,
      partner2Id: null,
      partner1Email: userEmail,
      partner2Email: null,
      status: 'pending',
      partner1Joined: true,
      partner2Joined: false,
      currentStage: 'individual',
      partner1IndividualComplete: false,
      partner2IndividualComplete: false,
      partner1ComparisonAcknowledged: false,
      partner2ComparisonAcknowledged: false,
      comparisonAcknowledgedAt: null,
      partner1JointAcknowledged: false,
      partner2JointAcknowledged: false,
      jointAcknowledgedAt: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      expiresAt: expiresAt.toISOString(),
    };

    // Store partner name as plaintext (per Data Classification spec)
    if (userName) {
      relationship.partner1Name = userName;
    }

    // Save relationship
    const relationshipRef = doc(db, 'relationships', relationshipId);
    await setDoc(relationshipRef, {
      ...relationship,
      createdAtTimestamp: serverTimestamp(),
      updatedAtTimestamp: serverTimestamp(),
    });

    // Create userRelationships mapping for partner1 immediately
    // This ensures partner1 can access the relationship right away
    const partner1RelationshipRef = doc(db, 'userRelationships', userId);
    await setDoc(partner1RelationshipRef, {
      userId,
      relationshipId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdAtTimestamp: serverTimestamp(),
      updatedAtTimestamp: serverTimestamp(),
    });

    // DON'T initialize responses yet - wait until relationship is active (when partner2 joins)

    return { relationshipId, pairingCode };
  } catch (error) {
    console.error('Error creating relationship:', error);
    throw error;
  }
}

/**
 * Generate 6-digit pairing code
 */
function generatePairingCode(): string {
  const code = Math.floor(100000 + Math.random() * 900000);
  return code.toString();
}

/**
 * Join existing relationship with pairing code
 */
export async function joinRelationshipWithCode(
  userId: string,
  userEmail: string,
  pairingCode: string,
  userName?: string
): Promise<string> {
  try {
    // Delegated to the joinRelationshipByCode Cloud Function.
    //
    // This used to query `relationships` by pairingCode from the browser, which
    // forced firestore.rules to let any signed-in user list documents belonging
    // to other people. Even scoped to status == 'pending' that exposed
    // partner1Email for every couple waiting for a partner, and there was no way
    // to throttle guesses against a 6-digit code.
    //
    // userId and userEmail are ignored — the function takes both from the
    // verified auth token rather than trusting the caller.
    const joinByCode = httpsCallable<
      { pairingCode: string; userName?: string },
      { relationshipId: string }
    >(functions, 'joinRelationshipByCode');

    const result = await joinByCode({ pairingCode, userName });
    return result.data.relationshipId;
  } catch (error) {
    console.error('Error joining relationship:', error);

    // PartnerPairing.tsx and JoinPartner.tsx render error.message directly, so
    // the callable's HttpsError message has to survive unwrapped. The Functions
    // SDK already exposes the server message as .message; this normalises the
    // internal/unknown cases so users never see a raw framework string.
    const message = error instanceof Error ? error.message : '';
    const isOpaque =
      !message ||
      message.toLowerCase().includes('internal') ||
      message.toLowerCase().includes('deadline');

    throw new Error(isOpaque ? 'Could not join with that code. Please try again.' : message);
  }
}

/**
 * Get relationship by ID
 */
export async function getRelationship(relationshipId: string): Promise<Relationship | null> {
  try {
    const relationshipRef = doc(db, 'relationships', relationshipId);
    const docSnap = await getDoc(relationshipRef);

    if (docSnap.exists()) {
      const data = docSnap.data();

      // Partner names are stored as plaintext strings (per Data Classification spec)
      // Handle legacy data: if name is an object (old partial encryption format), extract first name
      let partner1Name = data.partner1Name;
      let partner2Name = data.partner2Name;

      if (partner1Name && typeof partner1Name === 'object') {
        partner1Name = partner1Name.firstNames || JSON.stringify(partner1Name);
      }
      if (partner2Name && typeof partner2Name === 'object') {
        partner2Name = partner2Name.firstNames || JSON.stringify(partner2Name);
      }

      return {
        relationshipId: data.relationshipId,
        pairingCode: data.pairingCode,
        partner1Id: data.partner1Id,
        partner2Id: data.partner2Id,
        partner1Email: data.partner1Email,
        partner2Email: data.partner2Email,
        partner1Name,
        partner2Name,
        status: data.status,
        partner1Joined: data.partner1Joined,
        partner2Joined: data.partner2Joined,
        currentStage: data.currentStage,
        partner1IndividualComplete: data.partner1IndividualComplete,
        partner2IndividualComplete: data.partner2IndividualComplete,
        partner1ComparisonAcknowledged: data.partner1ComparisonAcknowledged,
        partner2ComparisonAcknowledged: data.partner2ComparisonAcknowledged,
        comparisonAcknowledgedAt: data.comparisonAcknowledgedAt,
        partner1ComfortLevel: data.partner1ComfortLevel,
        partner2ComfortLevel: data.partner2ComfortLevel,
        createdAt: timestampToString(data.createdAt),
        updatedAt: timestampToString(data.updatedAt),
        expiresAt: timestampToString(data.expiresAt),
      } as Relationship;
    }

    return null;
  } catch (error) {
    console.error('Error getting relationship:', error);
    return null;
  }
}

/**
 * Update relationship stage
 */
export async function updateRelationshipStage(
  relationshipId: string,
  stage: Relationship['currentStage']
): Promise<void> {
  try {
    const relationshipRef = doc(db, 'relationships', relationshipId);
    await updateDoc(relationshipRef, {
      currentStage: stage,
      updatedAt: new Date().toISOString(),
      updatedAtTimestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating relationship stage:', error);
    throw error;
  }
}

/**
 * Mark individual work as complete for a partner
 */
export async function markIndividualWorkComplete(
  relationshipId: string,
  partnerId: string
): Promise<void> {
  try {
    const relationship = await getRelationship(relationshipId);
    if (!relationship) {
      throw new Error('Relationship not found');
    }

    const isPartner1 = relationship.partner1Id === partnerId;
    const fieldToUpdate = isPartner1 ? 'partner1IndividualComplete' : 'partner2IndividualComplete';

    console.log('[markIndividualWorkComplete] Marking completion:', {
      partnerId,
      isPartner1,
      fieldToUpdate,
      relationshipId
    });

    const relationshipRef = doc(db, 'relationships', relationshipId);
    await updateDoc(relationshipRef, {
      [fieldToUpdate]: true,
      updatedAt: new Date().toISOString(),
      updatedAtTimestamp: serverTimestamp(),
    });

    console.log('[markIndividualWorkComplete] ✅ Successfully updated relationship document');

    // Verify the update
    const updatedRelationship = await getRelationship(relationshipId);
    console.log('[markIndividualWorkComplete] Verified updated relationship:', {
      partner1Complete: updatedRelationship?.partner1IndividualComplete,
      partner2Complete: updatedRelationship?.partner2IndividualComplete
    });
  } catch (error) {
    console.error('Error marking individual work complete:', error);
    throw error;
  }
}

/**
 * Unmark individual work as incomplete for a partner (when completion status changes)
 */
export async function unmarkIndividualWorkComplete(
  relationshipId: string,
  partnerId: string
): Promise<void> {
  try {
    const relationship = await getRelationship(relationshipId);
    if (!relationship) {
      throw new Error('Relationship not found');
    }

    const isPartner1 = relationship.partner1Id === partnerId;
    const fieldToUpdate = isPartner1 ? 'partner1IndividualComplete' : 'partner2IndividualComplete';

    console.log('[unmarkIndividualWorkComplete] Unmarking completion:', {
      partnerId,
      isPartner1,
      fieldToUpdate,
      relationshipId
    });

    const relationshipRef = doc(db, 'relationships', relationshipId);
    await updateDoc(relationshipRef, {
      [fieldToUpdate]: false,
      updatedAt: new Date().toISOString(),
      updatedAtTimestamp: serverTimestamp(),
    });

    console.log('[unmarkIndividualWorkComplete] ✅ Successfully updated relationship document');
  } catch (error) {
    console.error('Error unmarking individual work complete:', error);
    throw error;
  }
}

/**
 * Acknowledge comparison review
 */
export async function acknowledgeComparison(
  relationshipId: string,
  partnerId: string
): Promise<void> {
  try {
    const relationship = await getRelationship(relationshipId);
    if (!relationship) {
      throw new Error('Relationship not found');
    }

    const isPartner1 = relationship.partner1Id === partnerId;
    const fieldToUpdate = isPartner1 ? 'partner1ComparisonAcknowledged' : 'partner2ComparisonAcknowledged';

    const updates: Record<string, unknown> = {
      [fieldToUpdate]: true,
      updatedAt: new Date().toISOString(),
      updatedAtTimestamp: serverTimestamp(),
    };

    // If both acknowledged, set timestamp and advance stage
    if (
      (isPartner1 && relationship.partner2ComparisonAcknowledged) ||
      (!isPartner1 && relationship.partner1ComparisonAcknowledged)
    ) {
      updates.comparisonAcknowledgedAt = new Date().toISOString();
      updates.currentStage = 'joint';
    }

    const relationshipRef = doc(db, 'relationships', relationshipId);
    await updateDoc(relationshipRef, updates);
  } catch (error) {
    console.error('Error acknowledging comparison:', error);
    throw error;
  }
}

/**
 * Acknowledge joint collaboration completion by a partner
 * When both partners acknowledge, advances to 'review' stage
 */
export async function acknowledgeJointCollaboration(
  relationshipId: string,
  partnerId: string
): Promise<void> {
  try {
    const relationship = await getRelationship(relationshipId);
    if (!relationship) {
      throw new Error('Relationship not found');
    }

    const isPartner1 = relationship.partner1Id === partnerId;
    const fieldToUpdate = isPartner1 ? 'partner1JointAcknowledged' : 'partner2JointAcknowledged';

    const updates: Record<string, unknown> = {
      [fieldToUpdate]: true,
      updatedAt: new Date().toISOString(),
      updatedAtTimestamp: serverTimestamp(),
    };

    // If both acknowledged, set timestamp and advance stage
    if (
      (isPartner1 && relationship.partner2JointAcknowledged) ||
      (!isPartner1 && relationship.partner1JointAcknowledged)
    ) {
      updates.jointAcknowledgedAt = new Date().toISOString();
      updates.currentStage = 'review';
    }

    const relationshipRef = doc(db, 'relationships', relationshipId);
    await updateDoc(relationshipRef, updates);
  } catch (error) {
    console.error('Error acknowledging joint collaboration:', error);
    throw error;
  }
}

/**
 * Reset joint collaboration acknowledgments (when returning to edit)
 * Sets stage back to 'joint'
 */
export async function resetJointAcknowledgments(
  relationshipId: string
): Promise<void> {
  try {
    const relationshipRef = doc(db, 'relationships', relationshipId);
    await updateDoc(relationshipRef, {
      partner1JointAcknowledged: false,
      partner2JointAcknowledged: false,
      jointAcknowledgedAt: null,
      currentStage: 'joint',
      updatedAt: new Date().toISOString(),
      updatedAtTimestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error resetting joint acknowledgments:', error);
    throw error;
  }
}

/**
 * Set comfort level for a partner
 */
export async function setComfortLevel(
  relationshipId: string,
  partnerId: string,
  comfortLevel: number
): Promise<void> {
  try {
    const relationship = await getRelationship(relationshipId);
    if (!relationship) {
      throw new Error('Relationship not found');
    }

    const isPartner1 = relationship.partner1Id === partnerId;
    const fieldToUpdate = isPartner1 ? 'partner1ComfortLevel' : 'partner2ComfortLevel';

    console.log('[setComfortLevel] Setting comfort level:', {
      partnerId,
      isPartner1,
      comfortLevel,
      fieldToUpdate,
      relationshipId
    });

    const relationshipRef = doc(db, 'relationships', relationshipId);
    await updateDoc(relationshipRef, {
      [fieldToUpdate]: comfortLevel,
      updatedAt: new Date().toISOString(),
      updatedAtTimestamp: serverTimestamp(),
    });

    console.log('[setComfortLevel] ✅ Successfully set comfort level');
  } catch (error) {
    console.error('Error setting comfort level:', error);
    throw error;
  }
}

/**
 * Get comfort level for a partner
 */
export async function getComfortLevel(
  relationshipId: string,
  partnerId: string
): Promise<number | null> {
  try {
    const relationship = await getRelationship(relationshipId);
    if (!relationship) {
      return null;
    }

    const isPartner1 = relationship.partner1Id === partnerId;
    return isPartner1 ? relationship.partner1ComfortLevel || null : relationship.partner2ComfortLevel || null;
  } catch (error) {
    console.error('Error getting comfort level:', error);
    return null;
  }
}

/**
 * Get the joint comfort level — the most conservative (minimum) of the two
 * partners' levels. If only one partner has set a level, that level is used;
 * if neither has, defaults to 2 (Open).
 */
export async function getJointComfortLevel(
  relationshipId: string
): Promise<number> {
  try {
    const relationship = await getRelationship(relationshipId);
    if (!relationship) {
      return 2; // Default to Open if no relationship
    }

    const level1 = relationship.partner1ComfortLevel;
    const level2 = relationship.partner2ComfortLevel;

    // If both have set levels, use the lower (more conservative) level
    if (level1 && level2) {
      return Math.min(level1, level2);
    }

    // If only one has set, use that level
    if (level1) return level1;
    if (level2) return level2;

    // Default to level 2 (Open) if neither has set
    return 2;
  } catch (error) {
    console.error('Error getting joint comfort level:', error);
    return 2; // Default to Open on error
  }
}

/**
 * Save question selection to relationship
 */
export async function saveQuestionSelection(
  relationshipId: string,
  questionSelection: {
    individual: string[];
    joint: string[];
    createdAt: string;
    questionsPerTopic: number;
  }
): Promise<void> {
  try {
    const relationshipRef = doc(db, 'relationships', relationshipId);
    await updateDoc(relationshipRef, {
      questionSelection,
      updatedAt: new Date().toISOString(),
      updatedAtTimestamp: serverTimestamp(),
    });
    console.log('[saveQuestionSelection] ✅ Question selection saved successfully');
  } catch (error) {
    console.error('Error saving question selection:', error);
    throw error;
  }
}

/**
 * Get question selection from relationship
 */
export async function getQuestionSelection(
  relationshipId: string
): Promise<{
  individual: string[];
  joint: string[];
  createdAt: string;
  questionsPerTopic: number;
} | null> {
  try {
    const relationship = await getRelationship(relationshipId);
    return relationship?.questionSelection || null;
  } catch (error) {
    console.error('Error getting question selection:', error);
    return null;
  }
}

/**
 * Get the user's relationship id without creating one.
 * Returns null if the user has no relationship yet.
 */
export async function getUserRelationshipId(userId: string): Promise<string | null> {
  try {
    const userRelationshipSnap = await getDoc(doc(db, 'userRelationships', userId));
    return userRelationshipSnap.exists() ? userRelationshipSnap.data().relationshipId || null : null;
  } catch (error) {
    console.error('Error getting user relationship id:', error);
    return null;
  }
}

/**
 * Get or create a relationship for a user (auto-creates if needed)
 * This ensures every user has a relationship to store their individual work
 */
export async function getOrCreateUserRelationship(
  userId: string,
  userEmail: string,
  isAdmin: boolean = false
): Promise<string | null> {
  try {
    // First check userRelationships mapping (faster lookup)
    const userRelationshipRef = doc(db, 'userRelationships', userId);
    const userRelationshipSnap = await getDoc(userRelationshipRef);

    if (userRelationshipSnap.exists()) {
      return userRelationshipSnap.data().relationshipId;
    }

    // Fallback: Check if user already has a relationship as partner1.
    // limit(5) required by firestore.rules — the rule rejects unbounded list queries.
    const relationshipsRef = collection(db, 'relationships');
    const q = query(
      relationshipsRef,
      where('partner1Id', '==', userId),
      limit(5)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      // User already has a relationship
      return querySnapshot.docs[0].data().relationshipId;
    }

    // Also check if user joined as partner2
    const q2 = query(
      relationshipsRef,
      where('partner2Id', '==', userId),
      limit(5)
    );
    const querySnapshot2 = await getDocs(q2);

    if (!querySnapshot2.empty) {
      // User is partner2 in a relationship
      return querySnapshot2.docs[0].data().relationshipId;
    }

    // No relationship exists - create a solo relationship for the user
    console.log('[Storage] No relationship found for user, creating new relationship');

    // Get user's name from userProfiles
    const userProfileRef = doc(db, 'userProfiles', userId);
    const userProfileSnap = await getDoc(userProfileRef);
    const userName = userProfileSnap.exists() ? userProfileSnap.data().name : undefined;

    const { relationshipId } = await createRelationship(userId, userEmail, userName);
    console.log('[Storage] Created new relationship:', relationshipId);
    return relationshipId;
  } catch (error) {
    console.error('Error getting user relationship:', error);
    throw error;
  }
}

// ============= PRIVACY CONTROLS =============

/**
 * Toggle privacy for individual question
 */
export async function toggleQuestionPrivacy(
  relationshipId: string,
  userId: string,
  questionId: string,
  isPrivate: boolean
): Promise<void> {
  try {
    const partnerIndex = await getPartnerIndex(relationshipId, userId);
    const responsesRef = getUserResponsesRef(relationshipId, partnerIndex);
    const responses = await getUserResponses(relationshipId, userId);

    // Create response entry if it doesn't exist yet (for unanswered questions marked as private)
    if (!responses.responses[questionId]) {
      responses.responses[questionId] = {
        questionId,
        value: '', // Empty value, but marked as private
        updatedAt: new Date().toISOString(),
        isPrivate,
        sharedWithPartner: !isPrivate,
      };
    } else {
      // Update existing response
      responses.responses[questionId].isPrivate = isPrivate;
      responses.responses[questionId].sharedWithPartner = !isPrivate;
      responses.responses[questionId].updatedAt = new Date().toISOString();
    }

    responses.lastUpdated = new Date().toISOString();

    await setDoc(responsesRef, {
      ...responses,
      lastUpdatedTimestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error toggling question privacy:', error);
    throw error;
  }
}

/**
 * Toggle privacy for entire section (core topic)
 * @param relationshipId - The relationship ID
 * @param userId - The user ID
 * @param topicId - The core topic ID (e.g., 'emotions-connection', 'conflict-repair')
 * @param isPrivate - Whether to mark as private or not
 */
export async function toggleSectionPrivacy(
  relationshipId: string,
  userId: string,
  topicId: string,  // Changed from 'category' to 'topicId' - now expects core topic ID
  isPrivate: boolean
): Promise<void> {
  try {
    const partnerIndex = await getPartnerIndex(relationshipId, userId);
    const responsesRef = getUserResponsesRef(relationshipId, partnerIndex);
    const responses = await getUserResponses(relationshipId, userId);

    if (!responses.privateSections) {
      responses.privateSections = [];
    }

    if (isPrivate) {
      if (!responses.privateSections.includes(topicId)) {
        responses.privateSections.push(topicId);
        console.log(`[toggleSectionPrivacy] Marked topic '${topicId}' as private`);
      }
    } else {
      responses.privateSections = responses.privateSections.filter(s => s !== topicId);
      console.log(`[toggleSectionPrivacy] Unmarked topic '${topicId}' as private`);
    }

    await setDoc(responsesRef, {
      ...responses,
      lastUpdatedTimestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error toggling section privacy:', error);
    throw error;
  }
}

/**
 * Get privacy settings for user
 */
export async function getPrivacySettings(
  relationshipId: string,
  userId: string
): Promise<{ questions: string[]; sections: string[] }> {
  try {
    const responses = await getUserResponses(relationshipId, userId);
    const privateQuestions = Object.keys(responses.responses).filter(
      qId => responses.responses[qId].isPrivate
    );

    return {
      questions: privateQuestions,
      sections: responses.privateSections || [],
    };
  } catch (error) {
    console.error('Error getting privacy settings:', error);
    return { questions: [], sections: [] };
  }
}

// ============= JOINT COLLABORATION RESPONSES =============

/**
 * Gets (or creates on first use) the shared AES-256 key for a relationship's
 * joint responses.
 *
 * Envelope encryption: the raw key is never stored in Firestore. It is wrapped
 * (encrypted) separately under each partner's password-derived key and stored
 * as `partner1WrappedKey` / `partner2WrappedKey`. Each partner unwraps only
 * their own copy, so admins/operators who can read `relationshipKeys` cannot
 * recover the joint key — the wrapped blobs are useless without a partner's
 * password (which never leaves the browser).
 *
 * A `pendingKeyWrap` field temporarily holds the raw key (base64) so the
 * partner who is absent when the key is first created can wrap it under their
 * own key on their first save/read; it is deleted once wrapped.
 */
async function getOrCreateJointKey(relationshipId: string): Promise<CryptoKey | null> {
  if (!shouldEncrypt()) return null;

  const user = getCurrentUser();
  const myKey = getEncryptionKey();
  if (!user || !myKey) return null;

  // Determine this user's partner slot so we know which wrapped copy is theirs.
  const relationship = await getRelationship(relationshipId);
  if (!relationship) return null;
  const isPartner1 = relationship.partner1Id === user.id;
  const isPartner2 = relationship.partner2Id === user.id;
  if (!isPartner1 && !isPartner2) return null;
  const myField = isPartner1 ? 'partner1WrappedKey' : 'partner2WrappedKey';

  const keyRef = doc(db, 'relationshipKeys', relationshipId);
  const keyDoc = await getDoc(keyRef);
  const data = keyDoc.exists() ? keyDoc.data() : null;

  // 1) We already have our own wrapped copy — unwrap it.
  if (data?.[myField]) {
    try {
      const raw = await ClientEncryption.decrypt(data[myField], myKey);
      return ClientEncryption.importRelationshipKey(raw);
    } catch (error) {
      console.error('[getOrCreateJointKey] Failed to unwrap my wrapped key:', error);
      return null;
    }
  }

  // 1b) Legacy migration: records created before envelope encryption stored the
  //     raw key as `jointKey`. Wrap it under our key and preserve the raw value
  //     as `pendingKeyWrap` so the other partner can still complete their own
  //     wrap. This keeps pre-existing joint responses readable after rollout.
  if (data?.jointKey) {
    try {
      const wrapped = await ClientEncryption.encrypt(data.jointKey, myKey);
      await updateDoc(keyRef, {
        [myField]: wrapped,
        pendingKeyWrap: data.jointKey,
        jointKey: deleteField(),
      });
      return ClientEncryption.importRelationshipKey(data.jointKey);
    } catch (error) {
      console.error('[getOrCreateJointKey] Failed to migrate legacy jointKey:', error);
      return null;
    }
  }

  // 2) The other partner left a transient raw key for us — wrap it under our key.
  if (data?.pendingKeyWrap) {
    try {
      const wrapped = await ClientEncryption.encrypt(data.pendingKeyWrap, myKey);
      await updateDoc(keyRef, { [myField]: wrapped, pendingKeyWrap: deleteField() });
      return ClientEncryption.importRelationshipKey(data.pendingKeyWrap);
    } catch (error) {
      console.error('[getOrCreateJointKey] Failed to wrap pending key:', error);
      return null;
    }
  }

  // 3) No key yet — the first partner to save creates it, wraps it under their
  //    own key, and leaves a pending copy for the absent partner.
  const { key, rawBase64 } = await ClientEncryption.generateRelationshipKey();
  const wrapped = await ClientEncryption.encrypt(rawBase64, myKey);
  try {
    await setDoc(keyRef, {
      [myField]: wrapped,
      pendingKeyWrap: rawBase64,
      createdAt: serverTimestamp(),
    });
    return key;
  } catch {
    // Race condition: the other partner created it simultaneously — re-read and
    // either unwrap our own copy or wrap the pending copy.
    const retry = await getDoc(keyRef);
    if (!retry.exists()) return null;
    const retryData = retry.data();

    if (retryData[myField]) {
      try {
        const raw = await ClientEncryption.decrypt(retryData[myField], myKey);
        return ClientEncryption.importRelationshipKey(raw);
      } catch (error) {
        console.error('[getOrCreateJointKey] Race: failed to unwrap my copy:', error);
        return null;
      }
    }
    if (retryData.pendingKeyWrap) {
      try {
        const wrappedRetry = await ClientEncryption.encrypt(retryData.pendingKeyWrap, myKey);
        await updateDoc(keyRef, { [myField]: wrappedRetry, pendingKeyWrap: deleteField() });
        return ClientEncryption.importRelationshipKey(retryData.pendingKeyWrap);
      } catch (error) {
        console.error('[getOrCreateJointKey] Race: failed to wrap pending key:', error);
        return null;
      }
    }
    return null;
  }
}

/**
 * Save joint response for a relationship
 */
export async function saveJointResponse(
  relationshipId: string,
  questionId: string,
  value: string | string[]
): Promise<void> {
  try {
    console.log('[saveJointResponse] Saving:', { relationshipId, questionId, valueLength: typeof value === 'string' ? value.length : value });

    const jointResponsesRef = doc(db, 'jointResponses', relationshipId);
    const docSnap = await getDoc(jointResponsesRef);

    let responses: Record<string, { questionId: string; value: unknown; updatedAt: string; encrypted?: boolean }> = {};
    if (docSnap.exists()) {
      responses = docSnap.data().responses || {};
    }

    // Use the shared relationship key so both partners can decrypt each other's answers.
    // Joint responses are co-authored shared content — per-user keys would break cross-partner reads.
    let finalValue = value;
    let isEncrypted = false;
    const encryptionKey = await getOrCreateJointKey(relationshipId);

    // Encrypt if: encryption is enabled globally AND we have the shared key
    if (shouldEncrypt() && encryptionKey) {
      try {
        console.log(`🔒 [Joint] Encrypting field: ${questionId}`);
        if (typeof value === 'string') {
          finalValue = await ClientEncryption.encrypt(value, encryptionKey);
          isEncrypted = true;
        } else if (Array.isArray(value)) {
          // Encrypt each item in the array (e.g., for lists)
          finalValue = await Promise.all(
            value.map(async (item) => {
              if (typeof item === 'string') {
                return await ClientEncryption.encrypt(item, encryptionKey);
              }
              return item;
            })
          );
          isEncrypted = true;
        }
        console.log(`✅ [Joint] Field encrypted successfully`);
      } catch (encryptionError) {
        console.error(`❌ [Joint] Encryption failed for ${questionId}:`, encryptionError);
        // Fall back to saving unencrypted if encryption fails
      }
    }

    responses[questionId] = {
      questionId,
      value: finalValue,
      updatedAt: new Date().toISOString(),
      encrypted: isEncrypted,
    };

    await setDoc(jointResponsesRef, {
      relationshipId,
      responses,
      lastUpdated: new Date().toISOString(),
      lastUpdatedTimestamp: serverTimestamp(),
    });

    console.log('[saveJointResponse] ✅ Successfully saved joint response');
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    console.error('[saveJointResponse] ❌ Error saving joint response:', error);
    console.error('[saveJointResponse] Error code:', err?.code);
    console.error('[saveJointResponse] Error message:', err?.message);
    throw error;
  }
}

/**
 * Get all joint responses for a relationship
 */
export async function getJointResponses(
  relationshipId: string
): Promise<Record<string, { questionId: string; value: string | string[]; updatedAt: string; encrypted?: boolean }>> {
  try {
    const jointResponsesRef = doc(db, 'jointResponses', relationshipId);
    const docSnap = await getDoc(jointResponsesRef);

    if (docSnap.exists()) {
      const responses: Record<string, QuestionResponse> = docSnap.data().responses || {};
      // Use the shared relationship key — same key both partners use to encrypt/decrypt
      const encryptionKey = await getOrCreateJointKey(relationshipId);

      // Decrypt fields if we have the shared key
      if (encryptionKey) {
        for (const [questionId, response] of Object.entries(responses)) {
          if (response.encrypted && response.value) {
            try {
              console.log(`🔓 [Joint] Decrypting PII field: ${questionId}`);
              if (typeof response.value === 'string') {
                response.value = await ClientEncryption.decrypt(response.value, encryptionKey);
              } else if (Array.isArray(response.value)) {
                // Decrypt each item in the array
                response.value = await Promise.all(
                  response.value.map(async (item: unknown): Promise<string> => {
                    if (typeof item === 'string') {
                      return await ClientEncryption.decrypt(item, encryptionKey);
                    }
                    return String(item);
                  })
                );
              }
              // Remove encrypted flag after successful decryption (for UI)
              delete response.encrypted;
              console.log(`✅ [Joint] PII field decrypted successfully`);
            } catch (decryptionError) {
              console.error(`❌ [Joint] Decryption failed for ${questionId}:`, decryptionError);
              // Leave encrypted value as-is if decryption fails
            }
          }
        }
      }

      return responses;
    }

    return {};
  } catch (error) {
    console.error('Error getting joint responses:', error);
    return {};
  }
}

/**
 * Calculate joint collaboration progress
 */
export async function calculateJointProgress(
  relationshipId: string,
  totalQuestions?: number
): Promise<{ answered: number; total: number; percentage: number }> {
  try {
    // Load all joint questions from Firestore
    const allJointQuestions = await loadJointQuestions();

    // Get joint comfort level to filter questions
    const comfortLevel = await getJointComfortLevel(relationshipId);

    // Separate free and premium questions
    const freeQuestions = allJointQuestions.filter(q => !q.questionSet);
    const premiumQuestions = allJointQuestions.filter(q => !!q.questionSet);

    // Fixed free set: exactly the curated 24 questions, shown regardless of comfort level.
    const filteredQuestions = freeQuestions.filter(q => isFreeQuestion(q.id));

    // Add purchased premium sets, still comfort-filtered.
    // Use partner1 profile (both partners see same joint questions)
    const relationship = await getRelationship(relationshipId);
    const partner1Profile = relationship?.partner1Id ? await getUserProfile(relationship.partner1Id) : null;
    const purchasedPremium = premiumQuestions.filter(q => {
      if (!q.questionSet) return false;
      if (!hasPurchasedSet(partner1Profile, q.questionSet)) return false;
      return !q.comfortLevel || q.comfortLevel <= (comfortLevel || 3);
    });
    filteredQuestions.push(...purchasedPremium);

    const total = filteredQuestions.length;
    const responses = await getJointResponses(relationshipId);

    // Count answered questions from filtered set
    const answered = filteredQuestions.filter(q => {
      const response = responses[q.id];
      if (!response?.value) return false;

      const value = response.value;
      return Array.isArray(value)
        ? value.length > 0
        : typeof value === 'string'
          ? value.trim().length > 0
          : true;
    }).length;

    console.log('[calculateJointProgress] Joint comfort level:', comfortLevel);
    console.log('[calculateJointProgress] Filtered questions:', total);
    console.log('[calculateJointProgress] Answered:', answered);

    return {
      answered,
      total,
      percentage: total > 0 ? Math.round((answered / total) * 100) : 0,
    };
  } catch (error) {
    console.error('Error calculating joint progress:', error);
    // Cannot recover with filtered count - jointQuestions is not in scope
    // Return safe defaults using the optional totalQuestions parameter
    return { answered: 0, total: totalQuestions || 0, percentage: 0 };
  }
}

// ============= FLAG FOR REVIEW (COMPARISON) =============

/**
 * Flag a question for future review
 * Either partner can flag a conflicting answer to discuss later
 */
export async function flagQuestionForReview(
  relationshipId: string,
  questionId: string
): Promise<void> {
  try {
    const relationshipRef = doc(db, 'relationships', relationshipId);
    const relationshipSnap = await getDoc(relationshipRef);

    if (!relationshipSnap.exists()) {
      throw new Error('Relationship not found');
    }

    const relationship = relationshipSnap.data() as Relationship;
    const currentFlags = relationship.flaggedQuestions || [];

    // Add questionId if not already flagged
    if (!currentFlags.includes(questionId)) {
      await updateDoc(relationshipRef, {
        flaggedQuestions: [...currentFlags, questionId],
        updatedAt: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('[flagQuestionForReview] Error flagging question:', error);
    throw error;
  }
}

/**
 * Unflag a question (remove from review list)
 */
export async function unflagQuestionForReview(
  relationshipId: string,
  questionId: string
): Promise<void> {
  try {
    const relationshipRef = doc(db, 'relationships', relationshipId);
    const relationshipSnap = await getDoc(relationshipRef);

    if (!relationshipSnap.exists()) {
      throw new Error('Relationship not found');
    }

    const relationship = relationshipSnap.data() as Relationship;
    const currentFlags = relationship.flaggedQuestions || [];

    // Remove questionId from array
    const updatedFlags = currentFlags.filter(id => id !== questionId);

    await updateDoc(relationshipRef, {
      flaggedQuestions: updatedFlags,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('[unflagQuestionForReview] Error unflagging question:', error);
    throw error;
  }
}

/**
 * Toggle flag status for a question
 */
export async function toggleQuestionFlag(
  relationshipId: string,
  questionId: string
): Promise<boolean> {
  try {
    const relationshipRef = doc(db, 'relationships', relationshipId);
    const relationshipSnap = await getDoc(relationshipRef);

    if (!relationshipSnap.exists()) {
      throw new Error('Relationship not found');
    }

    const relationship = relationshipSnap.data() as Relationship;
    const currentFlags = relationship.flaggedQuestions || [];
    const isFlagged = currentFlags.includes(questionId);

    if (isFlagged) {
      await unflagQuestionForReview(relationshipId, questionId);
      return false;
    } else {
      await flagQuestionForReview(relationshipId, questionId);
      return true;
    }
  } catch (error) {
    console.error('[toggleQuestionFlag] Error toggling question flag:', error);
    throw error;
  }
}

// ============= GDPR DATA DELETION =============

/**
 * Comprehensively delete all user data for GDPR compliance
 * This includes:
 * - User profile
 * - All responses (individual and joint)
 * - Relationship data
 * - User-relationship mappings
 * - Signatures
 * - Authentication account
 *
 * @param userId - The user ID to delete all data for
 * @returns Promise that resolves when all data is deleted
 */
export async function deleteAllUserDataGDPR(userId: string): Promise<void> {
  try {
    console.log('[GDPR] Starting comprehensive data deletion for user:', userId);

    // Step 1: Get user's relationship ID
    const userRelDoc = await getDoc(doc(db, 'userRelationships', userId));
    let relationshipId: string | null = null;

    if (userRelDoc.exists()) {
      relationshipId = userRelDoc.data().relationshipId;
      console.log('[GDPR] Found relationship:', relationshipId);
    }

    // Step 2: Delete individual responses
    if (relationshipId) {
      try {
        const partnerIndex = await getPartnerIndex(relationshipId, userId);
        const responsesRef = getUserResponsesRef(relationshipId, partnerIndex);
        await deleteDoc(responsesRef);
        console.log('[GDPR] Deleted individual responses');
      } catch (error) {
        console.warn('[GDPR] Could not delete individual responses:', error);
      }
    }

    // Step 3: Delete joint responses if user is the only one in relationship
    if (relationshipId) {
      try {
        const relationship = await getRelationship(relationshipId);
        if (relationship) {
          // Check if both partners exist
          const hasPartner2 = relationship.partner2Id && relationship.partner2Id !== userId;

          if (!hasPartner2) {
            // User is alone in relationship, delete joint responses
            const jointResponsesRef = doc(db, 'jointResponses', relationshipId);
            await deleteDoc(jointResponsesRef);
            console.log('[GDPR] Deleted joint responses (user was alone)');
          } else {
            console.log('[GDPR] Keeping joint responses (partner still exists)');
          }
        }
      } catch (error) {
        console.warn('[GDPR] Could not check/delete joint responses:', error);
      }
    }

    // Step 4: Delete signatures
    if (relationshipId) {
      try {
        const signatureRef = doc(db, 'signatures', `${relationshipId}_${userId}`);
        await deleteDoc(signatureRef);
        console.log('[GDPR] Deleted signature');
      } catch (error) {
        console.warn('[GDPR] Could not delete signature:', error);
      }
    }

    // Step 5: Delete relationship if user is the only one or update relationship
    if (relationshipId) {
      try {
        const relationship = await getRelationship(relationshipId);
        if (relationship) {
          const hasPartner2 = relationship.partner2Id && relationship.partner2Id !== userId;

          if (!hasPartner2) {
            // User is alone, delete entire relationship
            await deleteDoc(doc(db, 'relationships', relationshipId));
            console.log('[GDPR] Deleted relationship (user was alone)');
          } else {
            // Partner exists, just remove this user's references
            const updates: Record<string, unknown> = {
              updatedAt: new Date().toISOString(),
              status: 'cancelled'
            };

            if (relationship.partner1Id === userId) {
              updates.partner1Id = null;
              updates.partner1Email = null;
              updates.partner1Name = null;
              updates.partner1Joined = false;
            } else {
              updates.partner2Id = null;
              updates.partner2Email = null;
              updates.partner2Name = null;
              updates.partner2Joined = false;
            }

            await updateDoc(doc(db, 'relationships', relationshipId), updates);
            console.log('[GDPR] Updated relationship to remove user');
          }
        }
      } catch (error) {
        console.warn('[GDPR] Could not delete/update relationship:', error);
      }
    }

    // Step 6: Delete user-relationship mapping
    try {
      await deleteDoc(doc(db, 'userRelationships', userId));
      console.log('[GDPR] Deleted user-relationship mapping');
    } catch (error) {
      console.warn('[GDPR] Could not delete user-relationship mapping:', error);
    }

    // Step 7: Delete user profile
    try {
      await deleteDoc(doc(db, 'userProfiles', userId));
      console.log('[GDPR] Deleted user profile');
    } catch (error) {
      console.warn('[GDPR] Could not delete user profile:', error);
    }

    // Step 8: Delete Firebase Auth account
    try {
      const user = auth.currentUser;
      if (user && user.uid === userId) {
        await firebaseDeleteUser(user);
        console.log('[GDPR] Deleted Firebase Auth account');
      } else {
        console.warn('[GDPR] Could not delete auth account - user not currently authenticated');
      }
    } catch (error) {
      console.warn('[GDPR] Could not delete Firebase Auth account:', error);
    }

    console.log('[GDPR] ✅ Comprehensive data deletion completed');
  } catch (error) {
    console.error('[GDPR] ❌ Error during comprehensive data deletion:', error);
    throw error;
  }
}

/**
 * Export all user data for GDPR data portability
 *
 * @param userId - The user ID to export data for
 * @returns JSON string containing all user data
 */
export async function exportAllUserDataGDPR(userId: string): Promise<string> {
  try {
    console.log('[GDPR] Starting data export for user:', userId);

    const exportData: {
      exportDate: string;
      userId: string;
      data: Record<string, unknown>;
    } = {
      exportDate: new Date().toISOString(),
      userId,
      data: {}
    };

    // Get user profile
    try {
      const profileDoc = await getDoc(doc(db, 'userProfiles', userId));
      if (profileDoc.exists()) {
        exportData.data.profile = profileDoc.data();
      }
    } catch (error) {
      console.warn('[GDPR] Could not export profile:', error);
    }

    // Get relationship
    try {
      const userRelDoc = await getDoc(doc(db, 'userRelationships', userId));
      if (userRelDoc.exists()) {
        const relationshipId = userRelDoc.data().relationshipId;
        exportData.data.relationshipMapping = userRelDoc.data();

        // Get relationship details
        const relationship = await getRelationship(relationshipId);
        if (relationship) {
          exportData.data.relationship = relationship;
        }

        // Get individual responses
        try {
          const partnerIndex = await getPartnerIndex(relationshipId, userId);
          const responsesRef = getUserResponsesRef(relationshipId, partnerIndex);
          const responsesDoc = await getDoc(responsesRef);
          if (responsesDoc.exists()) {
            exportData.data.individualResponses = responsesDoc.data();
          }
        } catch (error) {
          console.warn('[GDPR] Could not export individual responses:', error);
        }

        // Get joint responses
        try {
          const jointDoc = await getDoc(doc(db, 'jointResponses', relationshipId));
          if (jointDoc.exists()) {
            exportData.data.jointResponses = jointDoc.data();
          }
        } catch (error) {
          console.warn('[GDPR] Could not export joint responses:', error);
        }

        // Get signature
        try {
          const signatureDoc = await getDoc(doc(db, 'signatures', `${relationshipId}_${userId}`));
          if (signatureDoc.exists()) {
            exportData.data.signature = signatureDoc.data();
          }
        } catch (error) {
          console.warn('[GDPR] Could not export signature:', error);
        }
      }
    } catch (error) {
      console.warn('[GDPR] Could not export relationship data:', error);
    }

    console.log('[GDPR] ✅ Data export completed');
    return JSON.stringify(exportData, null, 2);
  } catch (error) {
    console.error('[GDPR] ❌ Error during data export:', error);
    throw error;
  }
}
